/**!
 * Created by Leonardo Cheslacki on 20/06/2024.
 *
 * MIT License
 *
 * Copyright (c) 2024 cheslacki
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.

 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import PropTypes from 'prop-types';

import StageController from './StageController';

import {HelpersPin} from './../helpers';

import Big from 'big.js';

import config from '../../../../config';

const {default: {domain, routes: {web}, scroll, text}} = config;

import WithType from './../helpers/WithType';

let _this = {};

/**
 *
 */
class StageScroll extends StageController {

    /**
     *
     * @param props
     * @param main
     */
    constructor(props, main = {}) {
        super(props);
        this.props = props;
        this.main = main;

        this.state = {
            data: [] || {},
            bitmap: null,
            pressed: false,
            invert: false,
            original: {
                occ_body: {
                    x: 0,
                    y: 0,
                    scale_x: 0,
                    scale_y: 0
                },
                occ_button: {
                    x: 0,
                    y: 0,
                    scale_x: 0,
                    scale_y: 0
                }
            },
            control: {
                big_speed_y: this.$controller.big_0,
                big_pressed_y: this.$controller.big_0,
                big_scroll_down_y: this.$controller.big_0,
                big_scroll_up_y: this.$controller.big_0,
                big_button_down_y: this.$controller.big_0,
                big_button_up_y: this.$controller.big_0
            }
        };

        this.$children_temp = [];

        _this = this;
    };

    $functions = {
        /**
         *
         * @param event
         * @param stage
         * @param occ_body
         * @param occ_button
         * @param occ_icon
         * @param big_button_friction
         */
        update: function (event, {stage, occ_body, occ_button, occ_icon, big_button_friction}) {
            let
                {pressed, control} = _this.state,
                {friction} = _this.props,
                big_mouseY = (new Big(stage.mouseY)),
                big_scroll_y = (new Big(occ_body.y)),
                big_friction = (new Big(friction)),
                big_button_speed_y = control.big_speed_y.times(big_button_friction);

            if (pressed) {
                control.big_speed_y = big_mouseY.minus(control.big_pressed_y);
            }

            control.big_pressed_y = big_mouseY;
            control.big_speed_y = control.big_speed_y.times(big_friction);

            if (big_scroll_y.lte(control.big_scroll_down_y)) {
                _this.moveOption(occ_body, occ_button, big_button_speed_y);
                occ_icon.occ_up.gotoAndStop('enabled');
            }

            if (big_scroll_y.gte(control.big_scroll_up_y)) {
                _this.moveOption(occ_body, occ_button, big_button_speed_y);
                occ_icon.occ_down.gotoAndStop('enabled');
            }

            if (!pressed) {
                if (big_scroll_y.gte(control.big_scroll_down_y)) {
                    occ_body.y = _this.bigToNumber(control.big_scroll_down_y);
                    occ_button.y = _this.bigToNumber(control.big_button_down_y);
                    occ_icon.occ_up.gotoAndStop('disabled');
                }

                if (big_scroll_y.lte(control.big_scroll_up_y)) {
                    occ_body.y = _this.bigToNumber(control.big_scroll_up_y);
                    occ_button.y = _this.bigToNumber(control.big_button_up_y);
                    occ_icon.occ_down.gotoAndStop('disabled');
                }
            }
        },

        /**
         *
         * @param event
         * @param stage
         * @param occ_body
         */
        handleScrollMouseDown: function (event, {stage, invert, content}) {
            _this.throttle('handleScrollMouseDown', () => {
                _this.setState({..._this.state, pressed: true, invert: invert}, () => {
                    const {control} = _this.state;
                    control.big_pressed_y = (new Big(stage.mouseY));
                    content.cursor = 'grabbing';
                });
            }, 100);
        },

        /**
         *
         * @param event
         * @param occ_body
         */
        handleScrollResetMouseDown: function (event, {content}) {
            _this.throttle('handleScrollResetMouseDown', () => {
                _this.setState({..._this.state, pressed: false}, () => {
                    content.cursor = 'grab';
                });
            }, 100);
        },

        /**
         *
         * @param event
         */
        handleScrollMouseOver: function (event) {
            _this.throttle('handleScrollMouseOver', () => {
                // console.log(event);
            }, 100);
        },

        /**
         *
         * @param event
         * @param _pin
         * @param _this_frame
         * @param properties
         * @param product
         */
        handleModalOpen: function (event, {_pin, _this_frame, properties, product}) {
            const
                {modal: {scale}} = _this.props,
                {bitmap} = _this.state,
                {uri} = _pin.state,
                {occ_modal, occ_modal_background, btn_close} = _this_frame;

            bitmap && occ_modal.removeChild(bitmap);

            _this.state.bitmap = (new createjs.Bitmap(uri));
            _this.state.bitmap.image.onload = () => {
                let
                    _scale = _this.verifyField(product, 'scale.value', false, scale),
                    scaleX = (_this.state.bitmap.scaleX * _this.toFloat(_scale)),
                    scaleY = (_this.state.bitmap.scaleY * _this.toFloat(_scale)),
                    bounds = _this.state.bitmap.getBounds(),
                    width = _this.calcSize(1, scaleX, bounds.width),
                    height = _this.calcSize(1, scaleY, bounds.height);

                _this.state.bitmap.scaleX = scaleX;
                _this.state.bitmap.scaleY = scaleY;

                occ_modal.x = _this.bigToNumber((new Big(properties.width)).minus(new Big(width)).div(_this.$controller.big_2), false, 'up');
                occ_modal.y = _this.bigToNumber((new Big(properties.height)).minus(new Big(height)).div(_this.$controller.big_2), false, 'up');

                occ_modal.addChild(_this.state.bitmap);
            }

            _this.$children_temp = _this_frame.children;

            _this_frame.children = _this_frame.children.filter((value) => {
                return (value.name === 'btn_close' || value.name === 'occ_modal' || value.name === 'occ_modal_background');
            });

            occ_modal_background.visible = true;

            btn_close.visible = true;

            _this.effectFadeIn(_this_frame, 'occ_modal');
        },

        handleModalClose: function () {
            const
                _this_frame = _this.getFrame(),
                {bitmap} = _this.state,
                {occ_modal, occ_modal_background, btn_close} = _this_frame;

            _this.effectFadeOut(_this_frame, 'occ_modal', () => {
                bitmap && occ_modal.removeChild(bitmap);

                _this_frame.children = _this.$children_temp;

                occ_modal_background.visible = false;

                btn_close.visible = false;
            });
        },

        handlePinsSwitch: function () {
            const {occ_icon_pins} = _this.getFrame();
            _this.switchPins(!occ_icon_pins.occ_more.isVisible())
        },

        handleSwitch: function (event, {product}) {

            const {occ_marker} = _this.get();

            if (_this.verifyField(product, 'frame.value') && occ_marker.labels.find(({label}) => (label === `frame0${product.frame.value}`))) {


                occ_marker.gotoAndStop(`frame0${product.frame.value}`);


                console.log(_this.getFrame(occ_marker));
            }


        }
    };

    /**
     *
     * @param occ_body
     * @param occ_button
     * @param big_button_speed_y
     */
    moveOption = (occ_body, occ_button, big_button_speed_y) => {
        let {invert, control} = this.state;

        if (invert) {
            occ_body.y -= this.bigToNumber(control.big_speed_y);
            occ_button.y += this.bigToNumber(big_button_speed_y);
        } else {
            occ_body.y += this.bigToNumber(control.big_speed_y);
            occ_button.y -= this.bigToNumber(big_button_speed_y);
        }
    };

    /**
     *
     * @param visible
     */
    switchPins = (visible = false) => {
        const {occ_plant, occ_icon_pins} = _this.getFrame();
        occ_icon_pins.occ_more.visible = visible;
        occ_icon_pins.occ_less.visible = !visible;
        occ_plant.occ_pins.visible = !visible;
    };

    clonePin = (_this) => {
        return {
            object: null,
            helpersPin: HelpersPin(this)({current_name: 'pins'}),

            /**
             *
             * @returns {null}
             */
            get pin() {
                return this.object;
            },

            /**
             *
             * @param object
             */
            set pin(object) {
                this.object = object;
            },

            clone() {
                const _this_frame = _this.getFrame(), {content_pin} = _this_frame.occ_map;
                this.pin = content_pin.clone();
            },

            /**
             *
             * @param number
             * @param x
             * @param y
             * @param color
             * @param font
             * @param size
             * @param weight
             */
            setNumber(number, x, y, color = null, font = null, size = null, weight = null) {
                const {occ_align: {occ_text: {occ_number}}} = this.object;
                this.helpersPin.setNumber(occ_number, number, x, y, color, font, size, weight);
            },

            /**
             *
             * @param color
             * @param occ
             */
            setColor(color, occ) {
                const occ_color = _this.verifyField(this.object, occ, false, []);
                this.helpersPin.setColor(occ_color, color);
            },

            /**
             *
             * @param x
             * @param y
             */
            setCoordinate(x, y) {
                this.helpersPin.setCoordinate(this.object, x, y);
            },

            /**
             *
             * @param visible
             */
            setAnimate(visible) {
                const {occ_align: {occ_effect}} = this.object;
                this.helpersPin.setAnimate(occ_effect, visible);
            },

            /**
             *
             * @param enabled
             * @param cursor
             * @param uri
             */
            setAction(enabled, cursor, uri) {
                this.helpersPin.setAction(_this, this.object, enabled, cursor, uri);
            }
        }
    };

    clonePinText = (_this) => {
        return {
            object: null,
            helpersPin: HelpersPin(_this)({current_name: 'pins'}),

            /**
             *
             * @returns {null}
             */
            get pinText() {
                return this.object;
            },

            /**
             *
             * @param object
             */
            set pinText(object) {
                this.object = object;
            },

            clone() {
                const _this_frame = _this.getFrame(), {content_pin_text} = _this_frame.occ_map;
                this.pinText = content_pin_text.clone();
            },

            /**
             *
             * @param string
             * @param color
             * @param font
             * @param size
             * @param weight
             * @param capitalize
             */
            setText(string, color, font, size, weight, capitalize) {
                const {occ_text} = this.object;
                this.helpersPin.setText(occ_text, string, color, font, size, weight, null, null, capitalize);
            },

            /**
             *
             * @param number
             * @param x
             * @param y
             * @param color
             * @param font
             * @param size
             * @param weight
             */
            setNumber(number, x, y, color = null, font = null, size = null, weight = null) {
                const {occ_align: {occ_text: {occ_number}}} = this.object;
                this.helpersPin.setNumber(occ_number, number, x, y, color, font, size, weight);
            },

            /**
             *
             * @param color
             * @param occ
             */
            setColor(color, occ) {
                const occ_color = _this.verifyField(this.object, occ, false, []);
                this.helpersPin.setColor(occ_color, color);
            },

            /**
             *
             * @param x
             * @param y
             */
            setCoordinate(x, y) {
                this.helpersPin.setCoordinate(this.object, x, y);
            },

            /**
             *
             * @param visible
             */
            setAnimate(visible) {
                const {occ_align: {occ_effect}} = this.object;
                this.helpersPin.setAnimate(occ_effect, visible);
            },

            /**
             *
             * @param enabled
             * @param cursor
             * @param uri
             */
            setAction(enabled, cursor, uri) {
                this.helpersPin.setAction(_this, this.object, enabled, cursor, uri);
            }
        }
    };

    /**
     *
     * @param height
     */
    scanControl = (height) => {
        const
            {original, control} = this.state,
            {friction} = this.props,
            _this_current = this.get(),
            {
                stage: {canvas},
                occ_form: {
                    occ_icon,
                    occ_thumb: {occ_button, occ_track},
                    content_body,
                    content_body: {stage, occ_body, occ_size},
                    occ_thumb
                }
            } = _this_current;

        let
            big_scroll_height = (new Big(height)),
            big_size_height = (new Big(occ_size.nominalBounds.height)),
            big_track_height = (new Big(occ_track.nominalBounds.height)),
            body_scaleY = this.calcSize(big_size_height, big_scroll_height, 1, true),
            button_scaleY = this.calcSize(big_scroll_height, big_size_height, 1),
            big_button_height = this.calcSize(1, button_scaleY, (new Big(occ_button.nominalBounds.height)), true);

        if (body_scaleY.gt(this.$controller.big_1)) {
            control.big_scroll_down_y = (new Big(original.occ_body.y));
            control.big_scroll_up_y = big_size_height.plus(control.big_scroll_down_y).minus(big_scroll_height);

            control.big_button_down_y = (new Big(occ_button.y));
            control.big_button_up_y = big_track_height.plus(control.big_button_down_y).minus(big_button_height.plus(control.big_button_down_y.times(this.$controller.big_2)));

            this.setHandleMouseDown(occ_thumb, this.$functions.handleScrollMouseDown, {
                stage: stage,
                content: occ_thumb,
                invert: true
            });

            this.setHandleMouseDown(content_body, this.$functions.handleScrollMouseDown, {
                stage: stage,
                content: content_body,
                invert: false
            });

            this._handleScrollResetMouseDown && stage.off('stagemouseup', this._handleScrollResetMouseDown);
            this._handleScrollResetMouseDown = stage.on('stagemouseup', this.$functions.handleScrollResetMouseDown, false, false, {
                content: content_body
            });

            this._handleScrollMouseOver && stage.off('mouseover', this._handleScrollMouseOver);
            this._handleScrollMouseOver = stage.on('mouseover', this.$functions.handleScrollMouseOver);

            this.main.$supportTouch({
                window: canvas.ownerDocument.defaultView,
                document: canvas.ownerDocument
            }).initTouch();

            // stage.enableMouseOver();
            // stage.mouseMoveOutside = true;
            createjs.Touch.enable(stage);
            // createjs.Ticker.timingMode = createjs.Ticker.RAF;

            this.destroyTicker('_handleScroll');

            let ticker = createjs.Ticker.on('tick', this.$functions.update, false, false, {
                stage: stage,
                occ_body: occ_body,
                occ_button: occ_button,
                occ_icon: occ_icon,
                big_button_friction: this.calcSize(1, button_scaleY, friction, true)
            });

            this.setReferenceTicker('_handleScroll', ticker);

            content_body.cursor = 'grab';

            occ_button.setTransform(occ_button.x, occ_button.y, occ_button.scaleX, button_scaleY);
        }
    };

    startScroll = () => {
        const
            {
                modal: {composition_name},
                pin: {number_x, number_y, color_disabled, color_enabled, color_round},
                text: {text_y, color_text, color_number, font, font_size, weight, capitalize}
            } = this.props,
            {data} = this.state,
            {occ_form: {content_body: {occ_body}}} = this.get(),
            _this_frame = this.getFrame(),
            {properties} = this.getComposition(composition_name).getLibrary(),
            {occ_map: {occ_plant: {occ_pins}}} = _this_frame;

        let
            y = 0,
            pin_default_color = color_disabled;

        occ_pins.removeAllChildren();

        occ_body.removeAllChildren();

        this.recursiveSyncMap(data, 0, data.length, (item, next, stop, index) => {
            const
                product = this.verifyField(item, 'product'),
                // pin = this.clonePin(this),
                pin_text = this.clonePinText(this),
                _id = this.verifyField(item, 'attributes.id').value,
                _allow = this.verifyField(item, 'attributes.allow').value,
                image = this.verifyField(product, 'src').value;

            const markers = this.verifyField(product, 'markers').value;

            //   console.log(product);

            if (this.hasString(_allow, 'scroll')) {
                image && (pin_default_color = color_enabled);

                // /**
                //  * Clone pin
                //  */
                // pin.clone();
                //
                // pin.setNumber(this.verifyField(product, 'number.value', false, 0), number_x, number_y, color_number);
                // pin.setColor(this.verifyField(product, 'tintColor.value', false, pin_default_color), 'occ_align.occ_color');
                // pin.setColor(color_round, 'occ_align.occ_background.occ_round');
                // pin.setColor(color_round, 'occ_align.occ_effect.occ_round');
                // pin.setColor(color_round, 'occ_align.occ_effect.occ_animate.occ_round_first');
                // pin.setColor(color_round, 'occ_align.occ_effect.occ_animate.occ_round_second');
                // pin.setCoordinate(this.verifyField(product, 'x.value', false, 0), this.verifyField(product, 'y.value', false, 0));

                /**
                 * Clone pin text
                 */
                pin_text.clone();

                if (!this.isIdCompound(_id)) {
                    /**
                     * Clone pin text
                     */
                    pin_text.setNumber(this.verifyField(product, 'number.value', false, 0), number_x, number_y, color_number);
                    pin_text.setText(this.verifyField(product, 'text.value'), color_text, font, font_size, weight, capitalize);
                    pin_text.setColor(this.verifyField(product, 'tintColor.value', false, pin_default_color), 'occ_align.occ_color');
                    pin_text.setColor(color_round, 'occ_align.occ_background.occ_round');
                    pin_text.setColor(color_round, 'occ_align.occ_effect.occ_round');
                    pin_text.setColor(color_round, 'occ_align.occ_effect.occ_animate.occ_round_first');
                    pin_text.setColor(color_round, 'occ_align.occ_effect.occ_animate.occ_round_second');
                    pin_text.setCoordinate(0, y);
                }

                if (image) {
                    // /**
                    //  * Clone pin
                    //  */
                    // pin.setAnimate(true);
                    // pin.setAction(true, 'pointer', `${this.$base_uri}/${web.uri_images}/${image}`);

                    /**
                     * Clone pin text
                     */
                    if (!this.isIdCompound(_id)) {
                        pin_text.setAnimate(true);
                        pin_text.setAction(true, 'pointer', `${this.$base_uri}/${web.uri_images}/${image}`);
                    }

                    let _index = this.padWithLeadingZeros(index, 2);

                    // this[`_handlePin${_index}`] && pin.pin.off('mousedown', this[`_handlePin${_index}`]);
                    // this[`_handlePin${_index}`] = pin.pin.on('mousedown', this.$functions.handleModalOpen, false, false, {
                    //     _pin: pin.pin,
                    //     _this_frame: _this_frame,
                    //     properties: properties,
                    //     product: product
                    // });

                    /**
                     * Clone pin text
                     */
                    if (!this.isIdCompound(_id)) {
                        this[`_handlePinText${_index}`] && pin_text.pinText.off('click', this[`_handlePinText${_index}`]);
                        this[`_handlePinText${_index}`] = pin_text.pinText.on('click', this.$functions.handleSwitch, false, false, {
                            _pin: pin_text.pinText,
                            _this_frame: _this_frame,
                            properties: properties,
                            product: product
                        });
                    }
                } else {
                    // /**
                    //  * Clone pin
                    //  */
                    // pin.setAnimate(false);
                    // pin.setAction(false, 'default', null);

                    /**
                     * Clone pin text
                     */
                    if (!this.isIdCompound(_id)) {
                        pin_text.setAnimate(false);
                        pin_text.setAction(false, 'default', null);
                    }
                }

                // /**
                //  * Clone pin
                //  */
                // occ_pins.addChild(pin.pin);

                occ_body.addChild(pin_text.pinText);

                y += text_y;
            }


            next(true);
        }, () => {
            this.scanControl(y);
        });

        // this._handleClose && btn_close.off('mousedown', this._handleClose);
        // this._handleClose = btn_close.on('mousedown', this.$functions.handleModalClose);
        //
        // this._handleSwitch && btn_pins.off('mousedown', this._handleSwitch);
        // this._handleSwitch = btn_pins.on('mousedown', this.$functions.handlePinsSwitch);
    };

    initScroll = () => {
        const
            {occ_form: {content_body: {occ_body}, occ_thumb: {occ_button}}} = this.get(),
            {xml} = this.props;

        this.getForm(`${this.$base_uri || domain}/${web.uri_xml}/${xml}`, {}, {
            done: (response) => {
                let
                    object = this.firstArray(this.xmlToJson(response)),
                    products = this.getObject(object, 'products');

                this.setState({
                    ...this.state,
                    data: products,
                    original: {
                        occ_body: {
                            x: occ_body.x,
                            y: occ_body.y,
                            scale_x: occ_body.scaleX,
                            scale_y: occ_body.scaleY
                        },
                        occ_button: {
                            x: occ_button.x,
                            y: occ_button.y,
                            scale_x: occ_button.scaleX,
                            scale_y: occ_button.scaleY
                        }
                    }
                }, this.startScroll);
            },
            fail: (response) => {
                console.log(response);
            },
            always: () => {
                // this.switchPins();
            }
        });
    };
}

/**
 *
 * @type {{}}
 */
StageScroll.propTypes = {
    current_name: PropTypes.string,
    xml: PropTypes.string,
    pin: PropTypes.shape({
        number_x: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number
        ]),
        number_y: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number
        ]),
        color_enabled: PropTypes.string,
        color_disabled: PropTypes.string,
        color_round: PropTypes.string
    }),
    text: PropTypes.shape({
        text_x: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number
        ]),
        text_y: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number
        ]),
        color_text: PropTypes.string,
        color_number: PropTypes.string,
        font: PropTypes.string,
        font_size: PropTypes.number,
        weight: PropTypes.oneOf(['normal', 'bold'])
    }),
    modal: PropTypes.shape({
        scale: PropTypes.number
    }),
    content_text: PropTypes.object
};

/**
 *
 * @type {{}}
 */
StageScroll.defaultProps = {
    current_name: null,
    ...scroll,
    text: {...scroll.text, ...text}
};

/**
 *
 * @param ClassObject
 * @param className {className: 'className'}
 * @returns {function(*): function({}=): *} pass _this ClassMain (required) and props {} (optional)
 */
export default WithType(StageScroll, {className: 'StageScroll'});
